import { useNavigate } from "react-router-dom";
import {
  Star, BadgeCheck, ShieldCheck, Sparkles, CheckCircle2, Zap,
  CalendarCheck, MapPin, Clock, Heart,
} from "lucide-react";
import { statusText, statusDotColor } from "@/lib/businessStatus";
import PinButton from "@/components/PinButton";

const fastReply = (rt) => /hour/i.test(rt || "");

// Up to 3 positive badges, priority-ordered.
function buildBadges(pro, workedTogether) {
  const out = [];
  if (workedTogether) out.push({ key: "worked", Icon: CheckCircle2, label: "Worked Together", cls: "bg-[#E1EDE7] text-[#245544]" });
  if (pro.neighborhood_trusted) out.push({ key: "trusted", Icon: ShieldCheck, label: "Neighborhood Trusted", cls: "bg-[#C79B6B]/18 text-[#7a5a30]" });
  if (pro.is_pro_plus) out.push({ key: "proplus", Icon: Sparkles, label: "Pro Plus", cls: "bg-[#222321] text-[#D6B977] border border-[#D6B977]" });
  if (pro.verified) out.push({ key: "verified", Icon: BadgeCheck, label: "Verified Profile", cls: "bg-[#EEF2EF] text-[#4E544D]" });
  if (fastReply(pro.response_time)) out.push({ key: "fast", Icon: Zap, label: "Responds Quickly", cls: "bg-amber-100 text-amber-800" });
  if (pro.business_status?.code === "accepting") out.push({ key: "available", Icon: CalendarCheck, label: "Available This Week", cls: "bg-[#E1EDE7] text-[#245544]" });
  return out.slice(0, 3);
}

// Lightweight "why this pro fits" — deferred to a future AI feature (client-intent form).
export default function RecommendedProCard({ pro, workedTogether = false, onPinChange }) {
  const navigate = useNavigate();
  const badges = buildBadges(pro, workedTogether);
  const status = statusText(pro.business_status);
  const dot = statusDotColor(pro.business_status);

  return (
    <div
      className="bg-white rounded-2xl border border-black/5 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1 flex flex-col overflow-hidden"
      data-testid={`rec-card-${pro.user_id}`}
    >
      <div className="p-5 flex flex-col flex-1">
        {/* Header: photo + name + meta */}
        <div className="flex items-start gap-3 mb-3">
          <div className="relative w-14 h-14 rounded-full overflow-hidden ring-2 ring-[#F2F4F2] shadow-sm shrink-0 bg-[#F2F4F2]">
            {pro.avatar_url ? (
              <img src={pro.avatar_url} alt={pro.business_name} className="w-full h-full object-cover" loading="lazy"/>
            ) : (
              <div className="w-full h-full grid place-items-center bg-[#2E6B57] text-[#FFFFFF] font-bold text-xl">
                {pro.business_name?.[0] || "?"}
              </div>
            )}
            {dot && (
              <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full ring-2 ring-white" style={{ background: dot }} title="Business status"/>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-[#222] text-base truncate leading-tight" style={{fontFamily:'Outfit'}}>{pro.business_name}</h3>
            <p className="text-xs text-[#222]/55 uppercase tracking-wider font-medium mt-0.5 truncate">{pro.category}</p>
            <p className="text-xs text-[#222]/50 mt-0.5 flex items-center gap-1 truncate">
              {pro.city && <><MapPin size={11} className="shrink-0"/> {pro.city}</>}
            </p>
          </div>
          {/* Heart rating */}
          <div className="flex items-center gap-1 text-sm shrink-0">
            <Heart size={13} className="fill-[#EF4444] text-[#EF4444]"/>
            <span className="font-bold">{pro.rating ? pro.rating.toFixed(1) : "New"}</span>
            {pro.review_count > 0 && <span className="text-[#222]/45 text-xs">({pro.review_count})</span>}
          </div>
        </div>

        {/* Tagline */}
        {pro.bio && (
          <p className="text-sm text-[#222]/65 leading-snug mb-3 line-clamp-2" data-testid={`rec-tagline-${pro.user_id}`}>
            {pro.bio}
          </p>
        )}

        {/* Badges */}
        {badges.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {badges.map(b => (
              <span key={b.key} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${b.cls}`} data-testid={`rec-badge-${b.key}-${pro.user_id}`}>
                <b.Icon size={10}/> {b.label}
              </span>
            ))}
          </div>
        )}

        {/* Quick details */}
        {(pro.response_time || status) && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#222]/60 mb-3">
            {pro.response_time && (
              <span className="inline-flex items-center gap-1"><Clock size={12} className="text-[#2E6B57]"/> Replies {pro.response_time.toLowerCase()}</span>
            )}
            {status && (
              <span className="inline-flex items-center gap-1 truncate max-w-full">{status}</span>
            )}
          </div>
        )}

        {/* Why this pro fits — deferred to future AI feature */}

        {/* Actions */}
        <div className="flex items-center gap-2 mt-auto">
          <button
            onClick={() => navigate(`/pro/${pro.user_id}`)}
            className="flex-1 py-2 rounded-full bg-[#222] text-white text-sm font-semibold hover:bg-black transition"
            data-testid={`rec-view-${pro.user_id}`}
          >
            View Profile
          </button>
          <PinButton pro={pro} size="lg" label="Pin to My Board" pinnedLabel="Pinned" onChange={onPinChange}/>
        </div>
      </div>
    </div>
  );
}
